import { useEffect, useState } from "preact/hooks";
import type { AccountResponse, DeviceSummary } from "../../shared/sync-format";
import { formatBytes } from "../pwa/update";
import { appEngine, describeSyncFix, describeSyncStatus, timeStamp } from "../sync";
import { navigate } from "./router";
import { useSyncSession, useSyncStatus } from "./sync-status";
import { ThemeControl } from "./theme-control";

/**
 * The Account screen (issue #26), reached from the About section's Sync card: who the Account is,
 * every Device signed in on it with when it was last seen, what it is using against the ceilings,
 * signing this Device out (with or without taking the Characters off it), and deleting the whole
 * Account behind a typed confirmation.
 *
 * Nothing here is reachable without a session: signing out drops back to the home screen.
 */
export function AccountPage() {
  const session = useSyncSession();
  const status = useSyncStatus();
  const [account, setAccount] = useState<AccountResponse>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [removeLocal, setRemoveLocal] = useState(false);
  const [deletion, setDeletion] = useState<{ confirmation: string; expiresAt: number } | null>(null);
  const [typed, setTyped] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (session === null) navigate({ page: "home" });
  }, [session]);

  useEffect(() => {
    if (!session) return;
    let live = true;
    setError(undefined);
    appEngine()
      .account()
      .then(
        (a) => live && setAccount(a),
        (e: unknown) => live && setError((e as Error).message),
      );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.account.id, reload]);

  if (!session) return null;

  const run = async (work: () => Promise<string | undefined>) => {
    setBusy(true);
    setMessage(undefined);
    try {
      const said = await work();
      if (said) setMessage(said);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const signOutDevice = (device: DeviceSummary) =>
    run(async () => {
      await appEngine().revokeDevice(device.id);
      if (device.current) {
        navigate({ page: "home" });
        return undefined;
      }
      setReload((n) => n + 1);
      return `“${device.name}” is signed out. Its next sync fails until it signs in again; everything already on it stays.`;
    });

  const signOutHere = () =>
    run(async () => {
      await appEngine().signOut({ removeLocal });
      navigate({ page: "home" });
      return undefined;
    });

  const startDeletion = () =>
    run(async () => {
      setTyped("");
      setDeletion(await appEngine().deleteAccountToken());
      return undefined;
    });

  const deleteAccount = () =>
    run(async () => {
      if (!deletion) return undefined;
      await appEngine().deleteAccount(deletion.confirmation);
      navigate({ page: "home" });
      return undefined;
    });

  const usage = account?.usage;

  return (
    <div class="sheet account-page">
      <header class="sheet-header">
        <a href="#/" class="icon back" aria-label="Characters">‹</a>
        <div class="sheet-title">
          <h1>Account</h1>
          <p class="muted">{session.account.email ?? session.account.name ?? "Signed in"}</p>
        </div>
        <ThemeControl />
      </header>
      {message && (
        <p class="notice sheet-notice" role="status">
          {message} <button type="button" class="linkish" onClick={() => setMessage(undefined)}>dismiss</button>
        </p>
      )}
      <main class="sheet-body">
        <section class="card">
          <p role="status"><strong>{describeSyncStatus(status)}</strong></p>
          {describeSyncFix(status) && <p class="muted">{describeSyncFix(status)}</p>}
          <p class="muted">
            Signed in with {session.account.issuer === "google" ? "Google" : session.account.issuer}
            {session.account.name ? ` as ${session.account.name}` : ""}
            {account ? ` · account opened ${timeStamp(account.account.createdAt)}` : ""}.
          </p>
        </section>

        <h2>Devices</h2>
        {error ? (
          <p class="notice" role="alert">The Account could not be read: {error}</p>
        ) : !account ? (
          <p class="muted">Reading…</p>
        ) : (
          <ul class="plain device-rows">
            {account.devices.map((device) => (
              <li key={device.id} class="card device-row">
                <span class="device-main">
                  <strong>{device.name}{device.current ? <> <span class="tag open">this device</span></> : null}</strong>
                  <span class="muted">Last seen {timeStamp(device.lastSeenAt)} · signed in {timeStamp(device.createdAt)}</span>
                </span>
                <span class="actions">
                  <ConfirmButton
                    label="Sign out"
                    armed={device.current ? "Sign this device out?" : `Sign ${device.name} out?`}
                    aria={`Sign out ${device.name}`}
                    disabled={busy}
                    onConfirm={() => void signOutDevice(device)}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}

        {usage && (
          <>
            <h2>Usage</h2>
            <div class="card usage">
              <p>
                {usage.documents.toLocaleString()} of {usage.ceilings.documents.toLocaleString()} documents (Characters, Bookmarks and Recents together, deletions included).
              </p>
              <progress max={usage.ceilings.documents} value={usage.documents} aria-label="Documents used" />
              <p>
                {formatBytes(usage.bytes)} of {formatBytes(usage.ceilings.accountBytes)}, and at most {formatBytes(usage.ceilings.documentBytes)} in one Character.
              </p>
              <progress max={usage.ceilings.accountBytes} value={usage.bytes} aria-label="Storage used" />
            </div>
          </>
        )}

        <h2>Sign out</h2>
        <div class="card">
          <p class="muted">
            This device stops syncing and keeps every Character, Bookmark and Recent it holds. The Account keeps its own copies, and signing in
            again puts the two back together.
          </p>
          <label class="check">
            <input type="checkbox" checked={removeLocal} onChange={(e) => setRemoveLocal((e.currentTarget as HTMLInputElement).checked)} />
            <span>Also remove the Characters, Bookmarks and Recents from this device</span>
          </label>
          {removeLocal && <p class="notice" role="status">Everything synced goes from this device. The Account keeps it all; another device, or signing in again, brings it back.</p>}
          <span class="actions">
            <ConfirmButton label="Sign out" armed={removeLocal ? "Sign out and remove?" : "Really sign out?"} aria="Sign out of this Account" disabled={busy} onConfirm={() => void signOutHere()} />
          </span>
        </div>

        <h2>Delete account</h2>
        <div class="card danger-card">
          {!deletion ? (
            <>
              <p class="muted">
                Deletes the Account itself: the server’s copy of every Character, Bookmark and Recent, every Device signed in on it, and the
                Account row. Characters already on this device stay on it, as they were before you signed in; other devices keep theirs too.
              </p>
              <span class="actions">
                <button type="button" class="danger" disabled={busy} onClick={() => void startDeletion()}>Delete account…</button>
              </span>
            </>
          ) : (
            <>
              <p><strong>This cannot be undone.</strong></p>
              <p class="muted">
                Going: every Character, Bookmark and Recent the Account holds, every deletion waiting in Recently Deleted, and all
                {account ? ` ${account.devices.length}` : ""} device{account && account.devices.length === 1 ? "" : "s"} signed in on it.
                Staying: everything already on this device.
              </p>
              <label class="field">
                <span class="muted">Type DELETE to confirm</span>
                <input type="text" value={typed} aria-label="Type DELETE to confirm" autoComplete="off" onInput={(e) => setTyped((e.currentTarget as HTMLInputElement).value)} />
              </label>
              <span class="actions">
                <button type="button" class="danger" disabled={busy || typed.trim().toUpperCase() !== "DELETE"} onClick={() => void deleteAccount()}>
                  Delete account
                </button>
                <button type="button" disabled={busy} onClick={() => { setDeletion(null); setTyped(""); }}>Cancel</button>
              </span>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

/** Two-tap confirmation, as the Character list's delete is: the first tap arms, the second acts. */
function ConfirmButton({ label, armed, aria, disabled, onConfirm }: { label: string; armed: string; aria: string; disabled?: boolean; onConfirm: () => void }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(() => setOn(false), 4000);
    return () => clearTimeout(t);
  }, [on]);
  return on ? (
    <button type="button" class="danger" disabled={disabled} aria-label={`Confirm: ${aria}`} onClick={() => { setOn(false); onConfirm(); }}>{armed}</button>
  ) : (
    <button type="button" disabled={disabled} aria-label={aria} onClick={() => setOn(true)}>{label}</button>
  );
}
